import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux'; // Import useSelector
import { selectIsAuthenticated, selectCurrentUser } from '../../features/auth/authSlice'; // Import auth selectors
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Pagination from '../../components/ui/Pagination';
import HostelFilters from './components/HostelFilters';
import { useGetHostelsQuery } from '../../features/api/apiSlice'; // Removed useGetUserDataQuery
import { useToast } from '../../components/ui/Toast'; // Import useToast

const Hostels = () => {
  const navigate = useNavigate();
  const addToast = useToast(); // Initialize useToast

  // Get user and authentication status from Redux store
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const user = useSelector(selectCurrentUser); // Get user from Redux store

  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    university: '',
    area: '',
    priceRange: [0, 500],
    roomType: '',
    amenities: [],
    availability: 'all'
  });

  const { data: hostelsData, isLoading, isSuccess, isError, error } = useGetHostelsQuery({
    offset: (currentPage - 1) * 8, // Assuming 8 items per page
    limit: 8,
    // Pass filters as API parameters
    city: filters.area, // Map 'area' filter to 'city' API parameter
    min_price: filters.priceRange[0],
    max_price: filters.priceRange[1],
    // Note: The backend API for get_hostels might need to be extended to support all these filters
  });

  const hostels = hostelsData?.data || [];
  const totalResults = hostelsData?.total_count || 0;
  const itemsPerPage = 8; // This should match the limit in useGetHostelsQuery
  const totalPages = Math.ceil(totalResults / itemsPerPage);

  const [selectedHostel, setSelectedHostel] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);

  // Handle API errors with toasts
  useEffect(() => {
    if (isError) {
      addToast(`Error fetching hostels: ${error?.message || 'Unknown error'}`, 'error');
    }
  }, [isError, error, addToast]);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleBookNow = (hostel) => {
    if (!isAuthenticated) {
      addToast('Please log in to book a hostel.', 'info');
      navigate('/login', { state: { from: window.location.pathname } });
      return;
    }
    setSelectedHostel(hostel);
    setShowBookingModal(true);
  };

  const handleViewDetails = (hostel) => {
    setSelectedHostel(hostel);
    navigate(`/hostels/${hostel.id}`); // Navigate to detailed hostel page
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Icon
        key={index}
        name="Star"
        size={14}
        className={index < Math.floor(rating) ? 'text-yellow-500 fill-current' : 'text-gray-300'}
      />
    ));
  };

  const getAvailabilityColor = (available, total) => {
    const percentage = (available / total) * 100;
    if (percentage >= 50) return 'text-green-600 bg-green-100';
    if (percentage >= 25) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  return (
    <div className="min-h-screen bg-background">
      <Header /> {/* user and onLogout props removed */}
      <main className="pt-16">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-green-500/10 to-blue-500/10 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Budget-Friendly Hostels
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Discover affordable accommodation with great amenities and social atmospheres across Uganda
            </p>
          </div>
        </div>

        {/* Hostel Filters */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <HostelFilters onFiltersChange={handleFiltersChange} currentFilters={filters} />
        </div>

        {/* Hostels Grid */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {isLoading ? (
            <p>Loading hostels...</p>
          ) : isError ? (
            <p>Error: {error?.message || 'Failed to load hostels.'}</p>
          ) : hostels.length === 0 ? (
            <p>No hostels found matching your criteria.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-8">
              {hostels.map((hostel) => (
                <div key={hostel.id} className="bg-card rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 group">
                  {/* Hostel Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={hostel.title_image} // Use title_image from API
                      alt={hostel.title} // Use title from API
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute top-4 right-4 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                      From UGX {hostel.price} {/* Assuming currency is UGX */}
                    </div>
                    {/* Room Availability Badge (mocked for now, as API doesn't provide this directly) */}
                    <div className={`absolute top-4 left-4 px-3 py-1 rounded-full text-sm font-medium ${getAvailabilityColor(hostel.availableRooms || 10, hostel.totalRooms || 20)}`}>
                      {hostel.availableRooms || 10}/{hostel.totalRooms || 20} rooms available
                    </div>
                  </div>

                  {/* Hostel Info */}
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-xl font-semibold text-foreground mb-1">{hostel.title}</h3>
                        <div className="flex items-center text-muted-foreground text-sm mb-2">
                          <Icon name="MapPin" size={14} className="mr-1" />
                          {hostel.address}
                        </div>
                      </div>
                      {/* Rating and Reviews (mocked for now) */}
                      <div className="flex items-center">
                        {renderStars(hostel.rating || 4.0)}
                        <span className="ml-2 text-sm text-muted-foreground">
                          ({hostel.reviews || 0})
                        </span>
                      </div>
                    </div>

                    <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                      {hostel.description}
                    </p>

                    {/* Room Availability Status (mocked for now) */}
                    <div className="mb-4">
                      <div className="flex items-center justify-between text-sm mb-2">
                        <span className="text-muted-foreground">Room Availability</span>
                        <span className={`font-medium px-2 py-1 rounded-full text-xs ${getAvailabilityColor(hostel.availableRooms || 10, hostel.totalRooms || 20)}`}>
                          {hostel.availableRooms || 10} of {hostel.totalRooms || 20} available
                        </span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-300 ${
                            ((hostel.availableRooms || 10) / (hostel.totalRooms || 20)) * 100 >= 50 ? 'bg-green-500' :
                            ((hostel.availableRooms || 10) / (hostel.totalRooms || 20)) * 100 >= 25 ? 'bg-yellow-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${((hostel.availableRooms || 10) / (hostel.totalRooms || 20)) * 100}%` }}
                        ></div>
                      </div>
                    </div>

                    {/* Amenities (mocked for now, as API doesn't provide this directly) */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {(hostel.amenities || ["WiFi", "Parking"]).slice(0, 3).map((amenity) => (
                        <span
                          key={amenity}
                          className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-full"
                        >
                          {amenity}
                        </span>
                      ))}
                      {(hostel.amenities || []).length > 3 && (
                        <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-full">
                          +{(hostel.amenities || []).length - 3} more
                        </span>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => handleViewDetails(hostel)}
                      >
                        <Icon name="Eye" size={16} className="mr-2" />
                        View Details
                      </Button>
                      <Button
                        size="sm"
                        className="flex-1"
                        onClick={() => handleBookNow(hostel)}
                        disabled={hostel.availableRooms === 0} // Mocked availability
                      >
                        <Icon name="Calendar" size={16} className="mr-2" />
                        {hostel.availableRooms === 0 ? 'Fully Booked' : 'Book Now'}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalResults > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalResults={totalResults}
              resultsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
            />
          )}
        </div>

        {/* Booking Modal */}
        {showBookingModal && selectedHostel && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-card rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-foreground">Book {selectedHostel.name}</h2>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowBookingModal(false)}
                  >
                    <Icon name="X" size={20} />
                  </Button>
                </div>

                {/* Availability Summary */}
                <div className="mb-6 p-4 bg-muted rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-foreground">Room Availability</span>
                    <span className={`font-medium px-3 py-1 rounded-full text-sm ${getAvailabilityColor(selectedHostel.availableRooms, selectedHostel.totalRooms)}`}>
                      {selectedHostel.availableRooms} of {selectedHostel.totalRooms} available
                    </span>
                  </div>
                  <div className="w-full bg-background rounded-full h-3">
                    <div
                      className={`h-3 rounded-full transition-all duration-300 ${
                        (selectedHostel.availableRooms / selectedHostel.totalRooms) * 100 >= 50 ? 'bg-green-500' :
                        (selectedHostel.availableRooms / selectedHostel.totalRooms) * 100 >= 25 ? 'bg-yellow-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${(selectedHostel.availableRooms / selectedHostel.totalRooms) * 100}%` }}
                    ></div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Available Room Types</h3>
                    <div className="space-y-3">
                      {/* Mock room types for now, as API doesn't provide this directly */}
                      {selectedHostel.roomTypes ? selectedHostel.roomTypes.map((room, index) => (
                        <div key={index} className="flex items-center justify-between p-4 border border-border rounded-lg">
                          <div>
                            <h4 className="font-medium text-foreground">{room.type}</h4>
                            <p className="text-sm text-muted-foreground">
                              {room.capacity} people • {room.available} available
                            </p>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-primary">
                              UGX {room.price}
                            </div>
                            <p className="text-sm text-muted-foreground">per night</p>
                          </div>
                        </div>
                      )) : (
                        <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                          <div>
                            <h4 className="font-medium text-foreground">Standard Room</h4>
                            <p className="text-sm text-muted-foreground">
                              2 people • 5 available
                            </p>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-primary">
                              UGX {selectedHostel.price}
                            </div>
                            <p className="text-sm text-muted-foreground">per night</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => setShowBookingModal(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="flex-1"
                      disabled={selectedHostel.availableRooms === 0}
                    >
                      <Icon name="Calendar" size={16} className="mr-2" />
                      {selectedHostel.availableRooms === 0 ? 'Fully Booked' : 'Proceed to Booking'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default Hostels;
